"use client";

import React, { useEffect, useRef, useState } from "react";

export type CostFactorItem = {
  title: string;
  impactLevel: "HIGH" | "MEDIUM" | "LOW" | string;
  percentage: number;
};

export type CostInfluencesProps = {
  title1?: React.ReactNode;
  title2?: React.ReactNode;
  description?: React.ReactNode;
  factorsArray: CostFactorItem[];
  noteText?: string;
};

// Impact-level specific theme
const IMPACT_THEME: Record<
  string,
  {
    ring: string;
    badgeBg: string;
    badgeText: string;
    accent: string;
  }
> = {
  high: {
    ring: "#225bd4",
    badgeBg: "bg-[#225bd4]/10",
    badgeText: "text-[#225bd4]",
    accent: "bg-[#225bd4]",
  },

  medium: {
    ring: "#e0a324",
    badgeBg: "bg-[#e0a324]/10",
    badgeText: "text-[#b8790f]",
    accent: "bg-[#e0a324]",
  },

  low: {
    ring: "#64748b",
    badgeBg: "bg-[#64748b]/10",
    badgeText: "text-[#475569]",
    accent: "bg-[#64748b]",
  },
};

function FactorCard({ item, index }: { item: CostFactorItem; index: number }) {
  const impactKey = item.impactLevel
    .toLowerCase()
    .replace(/\s*impact\s*$/i, "")
    .trim();

  const theme = IMPACT_THEME[impactKey] || IMPACT_THEME.low;

  const radius = 36;
  const circumference = 2 * Math.PI * radius;

  const cardRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const node = cardRef.current;

    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.3,
      },
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, []);

  const strokeDashoffset = isVisible
    ? circumference - (item.percentage / 100) * circumference
    : circumference;

  const impactLabel = impactKey.toUpperCase();

  return (
    <div
      ref={cardRef}
      className="group relative bg-white rounded-[16px] p-6 lg:p-8 flex items-center justify-between shadow-sm border border-[#ecf0f6] overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:border-transparent"
    >
      {/* Left Accent Bar */}
      <span
        className={`absolute left-0 top-0 bottom-0 w-1 ${theme.accent} opacity-70`}
        aria-hidden="true"
      />

      {/* Left Side: Title & Badge */}
      <div className="relative z-[1] flex flex-col gap-4 pl-2">
        <h3 className="clash-font text-[22px] lg:text-[24px] font-medium text-[#00040b]">
          {item.title}
        </h3>

        <div
          className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-md ${theme.badgeBg} ${theme.badgeText} text-[12px] font-semibold tracking-wider uppercase w-fit`}
        >
          {impactLabel} IMPACT
        </div>
      </div>

      {/* Right Side: Circular Progress Indicator */}
      <div className="relative z-[1] flex items-center justify-center shrink-0 w-[90px] h-[90px]">
        <svg
          className="w-full h-full transform -rotate-90"
          viewBox="0 0 90 90"
          aria-hidden="true"
        >
          {/* Background Track Circle */}
          <circle
            cx="45"
            cy="45"
            r={radius}
            stroke="#edf2f7"
            strokeWidth="8"
            fill="transparent"
          />

          {/* Impact-specific Progress Circle */}
          <circle
            cx="45"
            cy="45"
            r={radius}
            stroke={theme.ring}
            strokeWidth="8"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            fill="transparent"
            style={{
              transition: "stroke-dashoffset 1.1s ease-out",
              transitionDelay: `${index * 100}ms`,
            }}
          />
        </svg>

        {/* Percentage */}
        <span className="absolute font-semibold text-[#00040b] text-[16px] clash-font">
          {item.percentage}%
        </span>
      </div>
    </div>
  );
}

export default function CostInfluences({
  title1 = "What Influences The Cost?",
  title2 = (
    <>
      What Influences The <span className="text-[#225bd4]">Cost?</span>
    </>
  ),
  description = "The cost of mobile app development in USA depends on several factors such as features, third-party integrations, development timeline.",
  factorsArray,
  noteText = "Impact levels are indicative and vary by project.",
}: CostInfluencesProps) {
  return (
    <section className="relative bg-[#d9e2ed] py-[80px] lg:py-[100px] overflow-hidden">
      <div className="relative z-[1] w-[calc(100%_-_10%)] mx-auto max-w-[2500px] flex flex-col gap-[50px]">
        {/* Header Section */}
        <div className="flex flex-col gap-4">
          <h2 className="clash-font font-medium leading-[120%] text-[#00040b] text-[length:var(--section-title)]">
            {title2}
          </h2>

          <p className="text-[#00040b]/70 text-[length:var(--hero-para)] leading-[160%]">
            {description}
          </p>
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {factorsArray?.map((item, index) => (
            <FactorCard
              key={`${item.title}-${index}`}
              item={item}
              index={index}
            />
          ))}
        </div>

        {/* Footer Note */}
        {noteText && (
          <div className="flex items-center gap-2 text-[#00040b]/70 text-[14px]">
            <span className="inline-flex items-center justify-center shrink-0 w-6 h-6 rounded-full bg-[#225bd4]">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                {/* White Inner Circle */}
                <circle cx="12" cy="12" r="9" fill="white" />

                {/* Blue Info Icon */}
                <path
                  d="M12 10.5V16"
                  stroke="#225bd4"
                  strokeWidth="2"
                  strokeLinecap="round"
                />

                <circle cx="12" cy="7.5" r="1" fill="#225bd4" />
              </svg>
            </span>

            <span>{noteText}</span>
          </div>
        )}
      </div>
    </section>
  );
}
